import React, { useState } from "react";
import {
  Button,
  Form,
  Table,
  Switch,
  Popconfirm,
  Card,
  DatePicker,
  Dropdown,
  Grid,
  Spin,
  Empty,
  Pagination,
} from "antd";
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  HistoryOutlined,
  CalendarOutlined,
  FileTextOutlined,
  BarChartOutlined,
  ReloadOutlined,
  MoreOutlined,
} from "@ant-design/icons";
import EmployeeFormModal from "./EmployeeModal";
import { useEmployees } from "../../../hooks/useEmployee";
import { useExcuses } from "../../../hooks/useExcuse";
import EmployeeShiftsModal from "./EmployeeShiftModal";
import ExcuseRequestModal from "./ExcuseRequestModal";
import dayjs from "dayjs";
import { formatHoursAndMinutes } from "../../../utils/helpers";
import DoctorPErformance from "../Doctors/DoctorPerformance";

const { useBreakpoint } = Grid;
const PAGE_SIZE = 10;

const EmployeeManagement = () => {
  const screens = useBreakpoint();
  const isMobile = screens.md === false; // < 768px

  const [form] = Form.useForm();
  const {
    useGetEmployees,
    useCreateEmployee,
    useUpdateEmployee,
    useDeleteEmployee,
    useToggleShift,
  } = useEmployees();

  const { useAssignExcuse, useRemoveAssignment } = useExcuses();
  const [showDeleted, setShowDeleted] = useState(false);
  const [mobilePage, setMobilePage] = useState(1);
  const [employeeDateRange, setEmployeeDateRange] = useState([
    dayjs().startOf("month"),
    dayjs().endOf("month"),
  ]);

  const {
    data: employees = [],
    isLoading,
    isFetching,
    refetch: refetchEmployee,
  } = useGetEmployees(
    employeeDateRange?.[0] ? employeeDateRange[0].toISOString() : null,
    employeeDateRange?.[1] ? employeeDateRange[1].toISOString() : null,
    showDeleted || null,
  );

  const createMutation = useCreateEmployee();
  const updateMutation = useUpdateEmployee();
  const deleteMutation = useDeleteEmployee();
  const toggleMutation = useToggleShift();
  const assignExcuseMutation = useAssignExcuse();
  const removeAssignmentMutation = useRemoveAssignment();

  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingEmployee, setEditingEmployee] = useState(null);
  const [selectedShiftEmpId, setSelectedShiftEmpId] = useState(null);
  const [isShiftModalVisible, setIsShiftModalVisible] = useState(false);
  const [isExcuseRequestModalVisible, setIsExcuseRequestModalVisible] = useState(false);
  const [selectedEmployeeForRequest, setSelectedEmployeeForRequest] = useState(null);
  const [dateRange, setDateRange] = useState([
    dayjs().subtract(30, "days"),
    dayjs(),
  ]);
  const [isPerformanceModalVisible, setIsPerformanceModalVisible] = useState(false);
  const [selectedDoctorForPerformance, setSelectedDoctorForPerformance] = useState(null);

  const handleViewPerformance = (employee) => {
    setSelectedDoctorForPerformance(employee);
    setIsPerformanceModalVisible(true);
  };

  const { data: shiftData, isLoading: isLoadingShifts } =
    useEmployees().useGetEmployeeShifts(
      selectedShiftEmpId,
      dateRange?.[0] ? dateRange[0] : null,
      dateRange?.[1] ? dateRange[1] : null,
    );

  const getSalaryTypeLabel = (type) => {
    const types = { 0: "Month", 1: "Hour" };
    return types[type] || "Unknown";
  };

  const handleAdd = () => {
    setEditingEmployee(null);
    form.resetFields();
    setIsModalVisible(true);
  };

  const handleEdit = (employee) => {
    setEditingEmployee(employee);
    form.setFieldsValue({
      fullName: employee.fullName,
      email: employee.email,
      newPassword: undefined,
      jobTitle: employee.jobTitle,
      salaryType: employee.salaryType,
      salary: employee.salary,
      dailyHours: employee.dailyHours,
      weeklyDays: employee.weeklyDays,
      photoID: employee.photoID,
    });
    setIsModalVisible(true);
  };

  const handleExcuseAssignments = async (employeeId, excuseAssignments, existingAssignments = []) => {
    const toAdd = excuseAssignments.filter(
      (newAssignment) => !existingAssignments.some((existing) => existing.excuseId === newAssignment.excuseId),
    );
    const toRemove = existingAssignments.filter(
      (existing) => !excuseAssignments.some((newAssignment) => newAssignment.excuseId === existing.excuseId),
    );
    for (const assignment of toAdd) {
      await assignExcuseMutation.mutateAsync({
        employeeId,
        excuseId: assignment.excuseId,
        allowedDays: assignment.allowedDays,
      });
    }
    for (const assignment of toRemove) {
      await removeAssignmentMutation.mutateAsync({
        employeeId,
        excuseId: assignment.excuseId,
      });
    }
  };

  const handleSubmit = async (values) => {
    try {
      const { excuseAssignments, ...employeeData } = values;
      if (editingEmployee) {
        await updateMutation.mutateAsync({ id: editingEmployee.id, ...employeeData });
        if (excuseAssignments) {
          await handleExcuseAssignments(editingEmployee.id, excuseAssignments, editingEmployee.excuseAssignments || []);
        }
      } else {
        const newEmployee = await createMutation.mutateAsync(employeeData);
        if (excuseAssignments && excuseAssignments.length > 0 && newEmployee?.id) {
          await handleExcuseAssignments(newEmployee.id, excuseAssignments, []);
        }
      }
      setIsModalVisible(false);
    } catch (err) {
      console.error("Failed to save employee:", err);
    }
  };

  const handleViewShifts = (employee, id) => {
    setEditingEmployee(employee);
    setSelectedShiftEmpId(id);
    setIsShiftModalVisible(true);
  };

  const handleCreateExcuseRequest = (employee) => {
    setSelectedEmployeeForRequest(employee);
    setIsExcuseRequestModalVisible(true);
  };

  // ─── Shared actions menu (used by table + mobile cards) ─────────────────────
  const getActionItems = (record) => [
    {
      key: "excuse",
      label: "Create Excuse Request",
      icon: <FileTextOutlined />,
      onClick: () => handleCreateExcuseRequest(record),
    },
    {
      key: "shifts",
      label: "View Shifts",
      icon: <HistoryOutlined />,
      onClick: () => handleViewShifts(record, record.id),
    },
    {
      key: "edit",
      label: "Edit",
      icon: <EditOutlined />,
      onClick: () => handleEdit(record),
    },
    ...(record.role === 2 || record.role === 5
      ? [
          {
            key: "performance",
            label: "View Performance",
            icon: <BarChartOutlined />,
            onClick: () => handleViewPerformance(record),
          },
        ]
      : []),
    { type: "divider" },
    {
      key: "toggle",
      label: (
        <Popconfirm
          title={record.isDeleted ? "Restore Employee?" : "Deactivate Employee?"}
          description={
            record.isDeleted
              ? "Restore this employee to active?"
              : "Are you sure you want to deactivate this employee?"
          }
          onConfirm={() => deleteMutation.mutate(record.id)}
          okText="Yes"
          cancelText="No"
          okButtonProps={{ danger: !record.isDeleted }}
          overlayStyle={{ maxWidth: "calc(100vw - 24px)" }}
        >
          <span
            style={{ color: record.isDeleted ? "#22c55e" : "#ef4444" }}
            onClick={(e) => e.stopPropagation()}
          >
            {record.isDeleted ? "Restore" : "Deactivate"}
          </span>
        </Popconfirm>
      ),
      icon: (
        <span style={{ color: record.isDeleted ? "#22c55e" : "#ef4444" }}>
          {record.isDeleted ? <ReloadOutlined /> : <DeleteOutlined />}
        </span>
      ),
    },
  ];

  const renderActionsDropdown = (record) => (
    <Dropdown menu={{ items: getActionItems(record) }} trigger={["click"]} placement="bottomRight">
      <Button
        type="text"
        icon={<MoreOutlined style={{ fontSize: 18 }} />}
        loading={deleteMutation.isPending && deleteMutation.variables === record.id}
      />
    </Dropdown>
  );

  // ─── Desktop / tablet columns ───────────────────────────────────────────────
  const columns = [
    {
      title: "Name",
      dataIndex: "fullName",
      key: "fullName",
      fixed: "left",
      width: 150,
      render: (text, record) => (
        <div className="flex flex-col">
          <span className="font-semibold text-primary truncate">{text}</span>
          <span className="text-xs text-gray-500 truncate">{record.jobTitle}</span>
        </div>
      ),
    },
    {
      title: "Salary",
      dataIndex: "salary",
      width: 100,
      render: (salary, record) => (
        <div className="flex flex-col">
          <span className="font-medium text-primary">
            {salary}/{getSalaryTypeLabel(record.salaryType)}
          </span>
        </div>
      ),
    },
    {
      title: "Base",
      width: 90,
      render: (_, record) => (
        <div className="text-gray-600 text-center text-sm">
          {formatHoursAndMinutes(record.totalBaseHours) || 0}
        </div>
      ),
    },
    {
      title: "Extra",
      width: 90,
      render: (_, record) => (
        <div className="text-green-600 text-center text-sm">
          {formatHoursAndMinutes(record.totalExtraHours) || 0}
        </div>
      ),
    },
    {
      title: "Total",
      width: 80,
      render: (_, record) => (
        <div className="text-primary text-center text-sm">
          {formatHoursAndMinutes(record.totalWorkedHours) || 0}
        </div>
      ),
    },
    {
      title: "Days/Wk",
      width: 100,
      render: (_, record) => (
        <div className="text-primary text-center text-sm">{record.weeklyDays || 0}</div>
      ),
    },
    {
      title: "Hrs/Day",
      width: 90,
      render: (_, record) => (
        <div className="text-primary text-center text-sm">{record.dailyHours || 0}</div>
      ),
    },
    {
      title: "Status",
      dataIndex: "isInWorkNow",
      width: 100,
      render: (isInWorkNow) => (
        <span className={`text-sm font-semibold ${isInWorkNow ? "text-green-500" : "text-gray-500"}`}>
          {isInWorkNow ? "Working..." : "Working Off"}
        </span>
      ),
    },
    {
      title: "Actions",
      width: 60,
      render: (_, record) => renderActionsDropdown(record),
    },
  ];

  // ─── Mobile cards ───────────────────────────────────────────────────────────
  const Stat = ({ label, value, className = "text-primary" }) => (
    <div className="bg-gray-50 rounded-lg py-2 px-1 text-center min-w-0">
      <div className="text-[10px] text-gray-400 uppercase tracking-wide">{label}</div>
      <div className={`text-sm font-semibold break-words ${className}`}>{value}</div>
    </div>
  );

  const maxPage = Math.max(1, Math.ceil(employees.length / PAGE_SIZE));
  const currentMobilePage = Math.min(mobilePage, maxPage);
  const pagedEmployees = employees.slice(
    (currentMobilePage - 1) * PAGE_SIZE,
    currentMobilePage * PAGE_SIZE,
  );

  const renderMobileCards = () => (
    <Spin spinning={isLoading}>
      {!isLoading && employees.length === 0 ? (
        <Empty className="py-10" />
      ) : (
        <div className="flex flex-col gap-3 min-h-[120px]">
          {pagedEmployees.map((record) => (
            <div
              key={record.id}
              className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden"
            >
              <div className="h-1 bg-gradient-to-r from-primary to-primary/50" />
              <div className="p-3">
                {/* Header */}
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <h3 className="font-bold text-primary text-base leading-tight break-words">
                      {record.fullName}
                    </h3>
                    <p className="text-xs text-gray-500 mt-0.5 break-words">{record.jobTitle}</p>
                    <div className="flex flex-wrap items-center gap-2 mt-2">
                      <span
                        className={`text-xs font-semibold px-2 py-0.5 rounded-full ${
                          record.isInWorkNow
                            ? "bg-green-50 text-green-600"
                            : "bg-gray-100 text-gray-500"
                        }`}
                      >
                        {record.isInWorkNow ? "Working..." : "Working Off"}
                      </span>
                      {record.isDeleted && (
                        <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-red-50 text-red-500">
                          Inactive
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="shrink-0">{renderActionsDropdown(record)}</div>
                </div>

                {/* Salary */}
                <div className="mt-3 flex items-center justify-between bg-primary/5 rounded-lg px-3 py-2">
                  <span className="text-xs text-gray-500">Salary</span>
                  <span className="font-semibold text-primary text-sm">
                    {record.salary}/{getSalaryTypeLabel(record.salaryType)}
                  </span>
                </div>

                {/* Hours */}
                <div className="grid grid-cols-3 gap-2 mt-3">
                  <Stat
                    label="Base"
                    value={formatHoursAndMinutes(record.totalBaseHours) || 0}
                    className="text-gray-600"
                  />
                  <Stat
                    label="Extra"
                    value={formatHoursAndMinutes(record.totalExtraHours) || 0}
                    className="text-green-600"
                  />
                  <Stat
                    label="Total"
                    value={formatHoursAndMinutes(record.totalWorkedHours) || 0}
                  />
                </div>

                {/* Schedule */}
                <div className="grid grid-cols-2 gap-2 mt-2">
                  <Stat label="Days / Week" value={record.weeklyDays || 0} />
                  <Stat label="Hours / Day" value={record.dailyHours || 0} />
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {employees.length > PAGE_SIZE && (
        <div className="flex justify-center mt-4">
          <Pagination
            simple
            current={currentMobilePage}
            pageSize={PAGE_SIZE}
            total={employees.length}
            showSizeChanger={false}
            onChange={(page) => {
              setMobilePage(page);
              window.scrollTo({ top: 0, behavior: "smooth" });
            }}
          />
        </div>
      )}
    </Spin>
  );

  // ─── Filters ────────────────────────────────────────────────────────────────
  const renderFilters = () => (
    <div className="mb-4 flex flex-col gap-3 md:flex-row md:justify-between md:items-center">
      <div className="flex items-center gap-2 w-full md:w-auto">
        <CalendarOutlined className="text-primary shrink-0" />
        <div className="grid grid-cols-2 gap-2 flex-1 md:flex md:flex-none">
          <DatePicker
            placeholder="From Date"
            format="YYYY-MM-DD"
            value={employeeDateRange?.[0] || null}
            onChange={(date) => {
              setMobilePage(1);
              setEmployeeDateRange((prev) => [date, prev?.[1] || null]);
            }}
            className="border-primary w-full md:w-auto"
          />
          <DatePicker
            placeholder="To Date"
            format="YYYY-MM-DD"
            value={employeeDateRange?.[1] || null}
            onChange={(date) => {
              setMobilePage(1);
              setEmployeeDateRange((prev) => [prev?.[0] || null, date]);
            }}
            disabledDate={(current) =>
              employeeDateRange?.[0]
                ? current && current < employeeDateRange[0].startOf("day")
                : false
            }
            className="border-primary w-full md:w-auto"
          />
        </div>
      </div>

      <div className="flex items-center justify-between md:justify-end gap-2">
        <Switch
          checked={showDeleted}
          onChange={(val) => {
            setMobilePage(1);
            setShowDeleted(val);
          }}
          checkedChildren="InActive Employees"
          unCheckedChildren="Active Employees"
          style={{ backgroundColor: showDeleted ? "#ff4d4f" : "green" }}
        />
        <Button
          loading={isFetching}
          onClick={refetchEmployee}
          icon={<ReloadOutlined />}
          type="text"
          className="text-primary hover:text-primary_hover"
        />
      </div>
    </div>
  );

  return (
    <div className="p-0 sm:p-3 md:p-6  min-h-screen overflow-x-hidden">
      {/* Header */}
      <div className="flex flex-col gap-3 sm:flex-row sm:justify-between sm:items-center mb-4">
        <h1 className="text-lg md:text-2xl font-bold text-primary">Employee Management</h1>
        <Button
          icon={<PlusOutlined />}
          onClick={handleAdd}
          size="middle"
          className="bg-gradient-to-r text-white from-primary to-primary_hover hover:bg-primary_hover md:hover:scale-105 hover:to-[#166575] border-0 shadow-lg hover:shadow-xl rounded-xl font-semibold transition-all w-full sm:w-auto"
        >
          Add Employee
        </Button>
      </div>

      {isMobile ? (
        <>
          <Card className="shadow-sm mb-3 [&_.ant-card-body]:!p-3">{renderFilters()}</Card>
          {renderMobileCards()}
        </>
      ) : (
        <Card className="shadow-sm">
          {renderFilters()}
          <Table
            columns={columns}
            dataSource={employees}
            loading={isLoading}
            rowKey="id"
            bordered
            size="small"
            scroll={{ x: 1000 }}
            rowClassName={(record, index) => (index % 2 === 0 ? "bg-gray-50" : "")}
            pagination={{
              pageSize: PAGE_SIZE,
              showTotal: (total) => `Total ${total} employees`,
              showSizeChanger: true,
              pageSizeOptions: ["10", "20", "50"],
              responsive: true,
            }}
          />
        </Card>
      )}

      <EmployeeFormModal
        visible={isModalVisible}
        onCancel={() => setIsModalVisible(false)}
        onSubmit={handleSubmit}
        form={form}
        editingEmployee={editingEmployee}
      />

      <EmployeeShiftsModal
        visible={isShiftModalVisible}
        onCancel={() => { setIsShiftModalVisible(false); setSelectedShiftEmpId(null); }}
        data={shiftData}
        loading={isLoadingShifts}
        dateRange={dateRange}
        onDateChange={setDateRange}
        employee={editingEmployee}
      />

      <ExcuseRequestModal
        visible={isExcuseRequestModalVisible}
        onCancel={() => { setIsExcuseRequestModalVisible(false); setSelectedEmployeeForRequest(null); }}
        employee={selectedEmployeeForRequest}
      />

      <DoctorPErformance
        visible={isPerformanceModalVisible}
        onClose={() => { setIsPerformanceModalVisible(false); setSelectedDoctorForPerformance(null); }}
        doctor={selectedDoctorForPerformance}
      />
    </div>
  );
};

export default EmployeeManagement;